import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  Text,
  Button,
  TextInput,
  Checkbox,
  PasswordInput,
  Select,
  Title,
  Stack,
  Anchor,
  SimpleGrid,
  Group,
} from "@mantine/core";
import { useForm } from "@mantine/form";
import {
  IconUser,
  IconMail,
  IconPhone,
  IconLock,
  IconMapPin,
  IconBrandGoogle,
  IconCheck,
} from "@tabler/icons-react";
import { signInWithPopup } from "@firebase/auth";
import { auth, googleProvider } from "../../Lib/Firebase";
import { LoginResponse } from "../../Lib/Types";
import { motion } from "framer-motion";

import { AppName, Colors, LogoFullWhite } from "../../Lib/Data";
import {
  DefaultErrorNotification,
  DefaultSuccessNotification,
  ShowValidationError,
  trimString,
  validateEmail,
} from "../../Lib/Methods";

import "./styles.scss";
import { PerformRequest } from "../../Lib/usePerformRequest";
import { DefaultAuthResponse, NonPaginatedResponse } from "../../Lib/Responses";
import { Endpoints } from "../../Lib/Endpoints";
import { CountryCodes } from "../../Lib/CountryCodes";

const SignUp: React.FC = () => {
  const navigate = useNavigate();
  document.title = `Create An Account | ${AppName}`;

  interface SignupFormProps {
    fullName: string;
    email: string;
    phone: string;
    password: string;
    confirmPassword: string;
    country: string;
    gender: string;
  }
  const signupForm = useForm<SignupFormProps>({
    initialValues: {
      fullName: "",
      email: "",
      phone: "",
      password: "",
      confirmPassword: "",
      country: "Nigeria",
      gender: "male",
    },
    validate: {
      fullName: (value) =>
        value && value.length >= 4 ? null : "At LEAST 4 characters",
      email: (value) => (validateEmail(value) ? null : "Enter a valid email"),
      phone: (value) =>
        value && value.length > 6 ? null : "Enter Phone number",
      password: (value) =>
        value && value.length >= 6 ? null : "At LEAST 6 characters",
      confirmPassword: (value, values) =>
        value && value === values.password ? null : "Passwords do not match!",
      country: (value) =>
        value && value.length > 0 ? null : "Select a country",
    },
  });

  const [isLoading, setLoading] = useState<boolean>(false);
  const [isGoogleLoading, setGoogleLoading] = useState<boolean>(false);
  const [isAcceptTerms, setAcceptTerms] = useState<boolean>(false);

  const RegisterUser = async () => {
    const { hasErrors: formHasErrors } = signupForm.validate();
    if (!formHasErrors) {
      const {
        email,
        fullName: name,
        phone,
        password,
        country,
        gender,
      } = signupForm.values;
      setLoading(true);
      let data: {
        email: string;
        name: string;
        phone: string;
        password: string;
        country: string;
        gender?: string;
      } = {
        email: trimString(email, true),
        name: trimString(name),
        phone: trimString(phone),
        password: password,
        country: trimString(country),
      };
      if (["male", "female"].includes(gender)) {
        data = {
          ...data,
          gender: gender,
        };
      }
      const r = await PerformRequest<NonPaginatedResponse<DefaultAuthResponse>>(
        {
          method: "POST",
          route: Endpoints.GetSignUp(),
          data,
          callback: () => {
            setLoading(false);
          },
        }
      );
      setLoading(false);
      if (r) {
        if (r.status === 201) {
          DefaultSuccessNotification("Account created successfully!");
          localStorage.setItem("t", r.data.data.token);
          navigate("/d");
        } else if (r.status === 409) {
          DefaultErrorNotification("Email or Phone already taken!");
        } else if (r.status === 403) {
          ShowValidationError(r.data as unknown as string[]);
        } else {
          DefaultErrorNotification("An unknown error occurred!");
        }
      } else {
        DefaultErrorNotification("An error occurred!");
      }
    }
  };

  const handleGoogleSignup = async () => {
    try {
      setGoogleLoading(true);
      const result = await signInWithPopup(auth, googleProvider);
      const idToken = await result.user.getIdToken();

      const r = await PerformRequest<NonPaginatedResponse<LoginResponse>>({
        method: "POST",
        route: Endpoints.GetGoogleLogin(),
        data: {
          idToken,
          stayLoggedIn: true,
        },
        callback: () => {
          setGoogleLoading(false);
        },
      });

      if (r && (r.status === 200 || r.status === 201)) {
        localStorage.setItem("t", r.data.data.token);
        navigate("/d/services");
      } else {
        DefaultErrorNotification(r?.data?.message || "Google login failed");
      }
    } catch (error: any) {
      setGoogleLoading(false);
      if (error.code !== "auth/popup-closed-by-user") {
        DefaultErrorNotification(error.message || "Google login failed");
      }
    }
  };

  const features = [
    "Access 12+ premium streaming services",
    "Save up to 60% on every subscription",
    "Cancel or switch anytime — no strings",
    "Join a trusted community of 10K+ users",
  ];

  return (
    <div className="signup-wrapper">
      {/* ─── Brand Panel (Left) ─── */}
      <motion.div
        className="signup-brand-panel"
        initial={{ opacity: 0, x: -30 }}
        animate={{ opacity: 1, x: 0 }}
        transition={{ duration: 0.6, ease: "easeOut" }}
      >
        <div className="brand-content">
          <img src={LogoFullWhite} alt={AppName} className="brand-logo" />

          <div className="brand-text">
            <motion.h2
              className="brand-headline"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3, duration: 0.5 }}
            >
              Start saving on
              <br />
              premium subscriptions.
            </motion.h2>
            <motion.p
              className="brand-subtitle"
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.5, duration: 0.5 }}
            >
              Create your account in seconds and unlock shared access to all your
              favourite services.
            </motion.p>
          </div>

          <motion.div
            className="brand-features"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.7, duration: 0.5 }}
          >
            {features.map((feature, index) => (
              <motion.div
                key={index}
                className="feature-item"
                initial={{ opacity: 0, x: -10 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.8 + index * 0.1 }}
              >
                <div className="feature-icon">
                  <IconCheck size={14} stroke={2.5} />
                </div>
                <span className="feature-text">{feature}</span>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </motion.div>

      {/* ─── Form Panel (Right) ─── */}
      <div className="signup-form-panel">
        <motion.div
          className="signup-form-inner"
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.2 }}
        >
          <Stack gap={2} mb={28}>
            <Title order={2} fw={800} size={26}>
              Create Account
            </Title>
            <Text c="dimmed" size="sm" fw={400}>
              Fill in your details to get started
            </Text>
          </Stack>

          <form>
            <Stack gap={10}>
              <SimpleGrid cols={2} spacing="sm">
                <TextInput
                  label="Full Name"
                  placeholder="John Doe"
                  size="sm"
                  leftSection={<IconUser size={15} stroke={1.8} />}
                  {...signupForm.getInputProps("fullName")}
                  classNames={{ input: "auth-input" }}
                />
                <TextInput
                  label="Email"
                  placeholder="you@email.com"
                  size="sm"
                  leftSection={<IconMail size={15} stroke={1.8} />}
                  {...signupForm.getInputProps("email")}
                  classNames={{ input: "auth-input" }}
                />
              </SimpleGrid>

              <SimpleGrid cols={2} spacing="sm">
                <Select
                  label="Country"
                  placeholder="Select"
                  size="sm"
                  data={Array.from(
                    new Set(CountryCodes.map((c) => ({ ...c })))
                  ).map((c) => ({
                    label: `${c.flagEmoji} ${c.country}`,
                    value: c.country,
                  }))}
                  searchable
                  leftSection={<IconMapPin size={15} stroke={1.8} />}
                  {...signupForm.getInputProps("country")}
                  allowDeselect={false}
                  classNames={{ input: "auth-input" }}
                />
                <TextInput
                  label="Phone"
                  placeholder="8012345678"
                  size="sm"
                  leftSection={<IconPhone size={15} stroke={1.8} />}
                  {...signupForm.getInputProps("phone")}
                  type="number"
                  classNames={{ input: "auth-input" }}
                />
              </SimpleGrid>

              <Select
                label="Gender (Optional)"
                placeholder="Select"
                size="sm"
                data={[
                  { label: "Male", value: "male" },
                  { label: "Female", value: "female" },
                  { label: "Decline to say", value: "decline" },
                ]}
                {...signupForm.getInputProps("gender")}
                allowDeselect={false}
                classNames={{ input: "auth-input" }}
              />

              <SimpleGrid cols={2} spacing="sm">
                <PasswordInput
                  label="Password"
                  placeholder="Min 6 chars"
                  size="sm"
                  leftSection={<IconLock size={15} stroke={1.8} />}
                  {...signupForm.getInputProps("password")}
                  classNames={{ input: "auth-input" }}
                />
                <PasswordInput
                  label="Confirm"
                  placeholder="Re-enter"
                  size="sm"
                  leftSection={<IconLock size={15} stroke={1.8} />}
                  {...signupForm.getInputProps("confirmPassword")}
                  classNames={{ input: "auth-input" }}
                />
              </SimpleGrid>

              <Checkbox
                mt={4}
                label={
                  <Text size="xs" fw={500}>
                    I accept the{" "}
                    <Anchor
                      component={Link}
                      to="/terms"
                      target="_blank"
                      c={Colors.GreenPrimary}
                      fw={700}
                    >
                      Terms
                    </Anchor>{" "}
                    and{" "}
                    <Anchor
                      component={Link}
                      to="/privacy"
                      target="_blank"
                      c={Colors.GreenPrimary}
                      fw={700}
                    >
                      Privacy Policy
                    </Anchor>
                  </Text>
                }
                checked={isAcceptTerms}
                onChange={(e) => setAcceptTerms(e.currentTarget.checked)}
                color={Colors.GreenPrimary}
                size="xs"
              />

              <Button
                fullWidth
                mt="xs"
                color={Colors.GreenPrimary}
                onClick={RegisterUser}
                loading={isLoading}
                disabled={!isAcceptTerms}
                className="signup-btn"
              >
                Create Account
              </Button>

              <div className="flex-row align-center gap-sm" style={{ margin: "6px 0" }}>
                <div className="flex-1 divider-line"></div>
                <Text size="xs" c="dimmed" fw={600} px={8}>
                  or
                </Text>
                <div className="flex-1 divider-line"></div>
              </div>

              <Button
                fullWidth
                variant="default"
                leftSection={<IconBrandGoogle size={16} />}
                onClick={handleGoogleSignup}
                loading={isGoogleLoading}
                className="google-btn"
              >
                Continue with Google
              </Button>
            </Stack>
          </form>

          <Text ta="center" mt={24} size="sm" c="dimmed">
            Already have an account?{" "}
            <Anchor
              component="button"
              onClick={() => navigate("/login")}
              c={Colors.GreenPrimary}
              fw={700}
              size="sm"
            >
              Sign In
            </Anchor>
          </Text>
        </motion.div>
      </div>
    </div>
  );
};

export default SignUp;
